import { useEffect, useState } from 'react'
import { api, type ResolutionCaseFull, type ResolutionOutcome } from '../lib/api.ts'
import { formatCommaList, parseCommaList, sameCommaList } from '../lib/commaList.ts'
import { Button } from './Button.tsx'

/**
 * Writing a case by hand.
 *
 * Shaped like the comparison it will later produce -- ingredient on the left,
 * the product somebody picked in the middle, what should happen on the right
 * -- because that is the sentence a case actually asserts, and a form that
 * looked like a JSON file would make people fill in fields without seeing the
 * claim they were making.
 *
 * ## The catalogue comes first, and the selection is a dropdown
 *
 * That is the whole UX decision. A case is only true against a specific
 * catalogue: "Vitamina B12 resolves to 6187" means nothing unless 6187 is a
 * product that claims that synonym. Typing a product code by hand hides that
 * relationship and lets somebody write a case whose selection does not exist,
 * which grades as `unresolved` forever for a reason that has nothing to do
 * with the resolver.
 *
 * So the products are written down first, and every reference to one is
 * chosen from them. The invalid statuses are unreachable rather than validated
 * after the fact.
 */

const OUTCOMES: ReadonlyArray<{ value: ResolutionOutcome; label: string; hint: string }> = [
  {
    value: 'consistent',
    label: 'Concorda',
    hint: 'a escolha se sustenta; não dizer nada',
  },
  {
    value: 'suggest_correction',
    label: 'Sugere correção',
    hint: 'outro produto claramente vence; falar',
  },
  {
    value: 'unresolved',
    label: 'Não conclui',
    hint: 'não dá para saber; o silêncio é a resposta certa',
  },
]

export function ResolutionCaseEditor({
  initial,
  onSaved,
  onCancel,
}: {
  initial: ResolutionCaseFull
  onSaved: (saved: ResolutionCaseFull) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState<ResolutionCaseFull>(initial)
  const [error, setError] = useState<string | undefined>()
  const [problems, setProblems] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  const codes = draft.catalogue.map((p) => p.erpCode)

  function patch(next: Partial<ResolutionCaseFull>) {
    setDraft((current) => ({ ...current, ...next }))
  }

  async function save(status: 'draft' | 'corrected') {
    setBusy(true)
    setError(undefined)
    setProblems([])
    const next: ResolutionCaseFull = {
      ...draft,
      status,
      correctedAt: status === 'corrected' ? new Date().toISOString().slice(0, 10) : null,
    }
    try {
      await api.saveResolutionCase(next.caseId, next)
      onSaved(next)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao salvar')
      // The server refuses to mark a case corrected while it grades nothing,
      // and says which parts. Surfacing them is more useful than the refusal.
      const detail = (cause as { problems?: string[] } | undefined)?.problems
      if (Array.isArray(detail)) setProblems(detail)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-2">
        <h2 className="text-tPSemi text-text-strong">Sobre o caso</h2>
        <label className="flex flex-col gap-1">
          <span className="text-tTiny text-text-weak font-semibold uppercase">Observações</span>
          <textarea
            value={draft.notes}
            rows={2}
            placeholder="O que este caso testa, e por que ele importa."
            onChange={(event) => patch({ notes: event.target.value })}
            className="rounded-base border-stroke-weak bg-background-raised text-tP text-text-strong border px-3 py-2"
          />
        </label>
      </section>

      {/*
        First, and deliberately. Everything below refers to these products, so
        an empty catalogue makes the rest of the form meaningless -- and the
        order on screen is the order the thinking has to happen in.
      */}
      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-tPSemi text-text-strong">1. O catálogo da farmácia</h2>
          <span className="text-tSmall text-text-weak">
            Os produtos contra os quais este caso é julgado. Copie os nomes do ERP como eles são,
            inclusive quando forem abreviações ilegíveis — é aí que resolver é mais difícil.
          </span>
        </div>

        <div className="rounded-base border-stroke-weak flex flex-col border">
          {draft.catalogue.map((product, index) => (
            <div
              key={index}
              className="border-stroke-weak flex flex-wrap items-end gap-2 border-b p-2 last:border-b-0"
            >
              <Field
                label="Código"
                value={product.erpCode}
                width="w-28"
                placeholder="6187"
                onChange={(erpCode) =>
                  patch({
                    catalogue: draft.catalogue.map((p, i) => (i === index ? { ...p, erpCode } : p)),
                  })
                }
              />
              <Field
                label="Nome no ERP"
                value={product.name}
                width="flex-1 min-w-[12rem]"
                placeholder="ANALG SOD FR20"
                onChange={(name) =>
                  patch({
                    catalogue: draft.catalogue.map((p, i) => (i === index ? { ...p, name } : p)),
                  })
                }
              />
              <CommaListField
                label="Sinônimos (separados por vírgula)"
                values={product.synonyms}
                width="flex-1 min-w-[12rem]"
                placeholder="Dipirona, Dipirona Sódica"
                onChange={(synonyms) =>
                  patch({
                    catalogue: draft.catalogue.map((p, i) =>
                      i === index ? { ...p, synonyms } : p,
                    ),
                  })
                }
              />
              <Button
                variant="tertiary"
                tone="danger"
                size="small"
                onClick={() => patch({ catalogue: draft.catalogue.filter((_, i) => i !== index) })}
              >
                Remover
              </Button>
            </div>
          ))}
          {draft.catalogue.length === 0 && (
            <p className="text-tP text-text-weak p-3">
              Nenhum produto ainda. Um caso só é verdade contra um catálogo específico.
            </p>
          )}
        </div>

        <div>
          <Button
            variant="secondary"
            tone="neutral"
            size="small"
            onClick={() =>
              patch({ catalogue: [...draft.catalogue, { erpCode: '', name: '', synonyms: [] }] })
            }
          >
            Adicionar produto
          </Button>
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-tPSemi text-text-strong">
            2. O que a receita pede, e o que escolheram
          </h2>
          <span className="text-tSmall text-text-weak">
            Cada linha é uma afirmação: dado este ingrediente escrito assim, e este produto
            escolhido, o sistema deveria reagir assim.
          </span>
        </div>

        <div className="flex flex-col gap-2">
          {draft.ingredients.map((ingredient, index) => {
            const update = (next: Partial<(typeof draft.ingredients)[number]>) =>
              patch({
                ingredients: draft.ingredients.map((x, i) => (i === index ? { ...x, ...next } : x)),
              })

            return (
              <div
                key={index}
                className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-2 border p-3"
              >
                <div className="flex flex-wrap items-end gap-2">
                  <Field
                    label="Escrito na receita"
                    value={ingredient.prescribed}
                    width="flex-1 min-w-[10rem]"
                    placeholder="Vitamina B12"
                    onChange={(prescribed) => update({ prescribed })}
                  />
                  <Field
                    label="Dose"
                    value={ingredient.dose ?? ''}
                    width="w-28"
                    placeholder="500mg"
                    onChange={(dose) => update({ dose: dose.trim() === '' ? null : dose })}
                  />
                  <Picker
                    label="Produto escolhido"
                    value={ingredient.selected}
                    options={codes}
                    catalogue={draft.catalogue}
                    onChange={(selected) => update({ selected })}
                  />
                  <Button
                    variant="tertiary"
                    tone="danger"
                    size="small"
                    onClick={() =>
                      patch({ ingredients: draft.ingredients.filter((_, i) => i !== index) })
                    }
                  >
                    Remover
                  </Button>
                </div>

                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-tTiny text-text-weak font-semibold uppercase">
                      O sistema deveria
                    </span>
                    <select
                      value={ingredient.expect}
                      onChange={(event) => {
                        const expect = event.target.value as ResolutionOutcome
                        // A rival only means something for a correction, and
                        // leaving a stale one behind would assert a fix the
                        // case is no longer claiming.
                        update({
                          expect,
                          expectRival:
                            expect === 'suggest_correction' ? ingredient.expectRival : null,
                        })
                      }}
                      className="rounded-base border-stroke-weak bg-background-raised text-tP text-text-strong border px-3 py-2"
                    >
                      {OUTCOMES.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <span className="text-tSmall text-text-weak pb-2">
                    {OUTCOMES.find((o) => o.value === ingredient.expect)?.hint}
                  </span>

                  {ingredient.expect === 'suggest_correction' && (
                    <Picker
                      label="Deveria sugerir"
                      value={ingredient.expectRival ?? ''}
                      options={codes.filter((code) => code !== ingredient.selected)}
                      catalogue={draft.catalogue}
                      onChange={(expectRival) =>
                        update({ expectRival: expectRival === '' ? null : expectRival })
                      }
                    />
                  )}
                </div>

                <Field
                  label="Por que esta é a resposta certa"
                  value={ingredient.why}
                  width="w-full"
                  placeholder="Um sinônimo aprovado do produto escolhido; o nome no ERP é ilegível e isso não pode importar."
                  onChange={(why) => update({ why })}
                />
              </div>
            )
          })}

          {draft.ingredients.length === 0 && (
            <p className="text-tP text-text-weak">Nenhum ingrediente ainda.</p>
          )}
        </div>

        <div>
          <Button
            variant="secondary"
            tone="neutral"
            size="small"
            disabled={draft.catalogue.length === 0}
            title={
              draft.catalogue.length === 0
                ? 'Escreva o catálogo primeiro: um ingrediente aponta para um produto dele'
                : undefined
            }
            onClick={() =>
              patch({
                ingredients: [
                  ...draft.ingredients,
                  {
                    prescribed: '',
                    dose: null,
                    selected: codes[0] ?? '',
                    expect: 'consistent',
                    expectRival: null,
                    why: '',
                  },
                ],
              })
            }
          >
            Adicionar ingrediente
          </Button>
        </div>
      </section>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}
      {problems.length > 0 && (
        <ul className="text-tSmall text-text-error flex flex-col gap-1">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {/*
          Two saves, because "conferido" is a claim about the case being fit to
          measure against, not a synonym for saved. Nothing is scored against a
          draft, so saving one has to stay easy while it is half-written.
        */}
        <Button
          variant="primary"
          tone="brand"
          size="small"
          disabled={busy}
          onClick={() => void save('corrected')}
        >
          {busy ? 'Salvando...' : 'Salvar como conferido'}
        </Button>
        <Button
          variant="secondary"
          tone="neutral"
          size="small"
          disabled={busy}
          onClick={() => void save('draft')}
        >
          Salvar rascunho
        </Button>
        <Button variant="tertiary" tone="neutral" size="small" disabled={busy} onClick={onCancel}>
          Cancelar
        </Button>
        <span className="text-tSmall text-text-weak">
          Nada é medido contra um rascunho — só casos conferidos entram na pontuação.
        </span>
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  width,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  width: string
}) {
  return (
    <label className={`flex flex-col gap-1 ${width}`}>
      <span className="text-tTiny text-text-weak font-semibold uppercase">{label}</span>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-base border-stroke-weak bg-background-raised text-tP text-text-strong border px-3 py-2"
      />
    </label>
  )
}

/**
 * A list typed on one line, which survives the comma that separates it.
 *
 * The text and the values are two things here, and that is the whole point.
 * This field used to render `values.join(', ')` and parse every keystroke
 * straight back into `values`, so the comma somebody pressed was parsed away
 * and the input redrawn without it: a second synonym could not be typed at
 * all. The typed text stays as typed, and is only replaced when the values
 * arriving mean something it does not -- a reload, or another case.
 *
 * It settles on the way out: leaving the field tidies stray separators into
 * the canonical line, so nobody is left looking at "a,,b ,".
 */
function CommaListField({
  label,
  values,
  onChange,
  placeholder,
  width,
}: {
  label: string
  values: readonly string[]
  onChange: (values: string[]) => void
  placeholder?: string
  width: string
}) {
  const [typed, setTyped] = useState(() => formatCommaList(values))

  useEffect(() => {
    if (!sameCommaList(typed, values)) setTyped(formatCommaList(values))
    // Deliberately not on `typed`: this asks whether what arrived still means
    // what is on screen, and running it as somebody types is how a field
    // rewrites itself under the cursor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  return (
    <label className={`flex flex-col gap-1 ${width}`}>
      <span className="text-tTiny text-text-weak font-semibold uppercase">{label}</span>
      <input
        value={typed}
        placeholder={placeholder}
        onChange={(event) => {
          setTyped(event.target.value)
          onChange(parseCommaList(event.target.value))
        }}
        onBlur={() => setTyped(formatCommaList(parseCommaList(typed)))}
        className="rounded-base border-stroke-weak bg-background-raised text-tP text-text-strong border px-3 py-2"
      />
    </label>
  )
}

/**
 * A product, chosen from this case's own catalogue.
 *
 * Never a free-text code. Typing one hides the relationship the case depends
 * on and lets somebody name a product that does not exist here, which grades
 * as `unresolved` forever for a reason that is about the case rather than the
 * resolver -- and looks like a finding.
 */
function Picker({
  label,
  value,
  options,
  catalogue,
  onChange,
}: {
  label: string
  value: string
  options: readonly string[]
  catalogue: ReadonlyArray<{ erpCode: string; name: string }>
  onChange: (value: string) => void
}) {
  return (
    <label className="flex min-w-[12rem] flex-col gap-1">
      <span className="text-tTiny text-text-weak font-semibold uppercase">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-base border-stroke-weak bg-background-raised text-tP text-text-strong border px-3 py-2"
      >
        <option value="">—</option>
        {options.map((code) => {
          const product = catalogue.find((p) => p.erpCode === code)
          return (
            <option key={code} value={code}>
              {code}
              {product === undefined || product.name === '' ? '' : ` · ${product.name}`}
            </option>
          )
        })}
      </select>
    </label>
  )
}
